import { get, set } from "es-toolkit/compat";

export type Searchable = {
  hasMatch?: boolean;
};

type DefinityTreeMeta = {
  name: string;
  hasMatch?: boolean;
  count?: number;
  assetType: string;
};

export type TreeNode = {
  [key: string]: TreeNode;
} & {
  ___DEFINITY_TREE_META___: DefinityTreeMeta;
};

export interface TreeItem<T extends TreeItem<T>> {
  path: string;
  title: string;
  children?: T[];
  subtitle?: string;
  asset_type?: string;
  profile?: string;
}

export interface TreeItemClientEnhancements
  extends TreeItem<TreeItemClientEnhancements> {
  parent?: TreeItemClientEnhancements;
  path: string;
  label: string;
}

export const assetTreeToHashMap = (assetsData) => {
  const rootNode = {
    path: "root",
    title: "root",
    label: "root",
    children: assetsData,
  };

  return treeToHashMap(rootNode);
};

export const treeToHashMap = (
  node: TreeItemClientEnhancements,
  result: Record<string, TreeItemClientEnhancements> = {}
): Record<string, TreeItemClientEnhancements> => {
  result[node.path] = node;
  node.label = node.title;

  if (node.children) {
    node.children.forEach((child) => {
      child.parent = node;
      treeToHashMap(child, result);
    });
  }

  return result;
};

// DFS traverse for metadataReduce
export function dfsTree<node extends { children?: node[] }>(
  node: node,
  callback: (node: node) => void
) {
  if (node.children) {
    for (const child of node.children) {
      dfsTree(child, callback);
    }
  }
  callback(node);
}

export const assetNamesToTree = (
  names: string[],
  metas: Omit<DefinityTreeMeta, "name">[],
  rootId: string,
  search?: string | RegExp
): { node: TreeNode; matchedSearch: string[] } => {
  console.assert(
    names.length === metas.length,
    "Different lengths of names and metas",
    {
      names,
      metas,
    }
  );

  const matchedSearch = [] as string[];

  return {
    matchedSearch,
    node: names.reduce<TreeNode>(
      (node, name, i) => {
        const meta = { ...metas[i] } as DefinityTreeMeta;
        if (search && name.match(search)) {
          matchedSearch.push(node.___DEFINITY_TREE_META___.name);
          meta.hasMatch = true;
          set(node, `___DEFINITY_TREE_META___.hasMatch`, true);
        }

        if (/^\w+:\/\//.test(name)) {
          node[name] = {
            ___DEFINITY_TREE_META___: { ...meta, name },
          } as TreeNode;

          return node;
        }
        const prevValue = get(node, `${name}.___DEFINITY_TREE_META___`);

        set(node, `${name}.___DEFINITY_TREE_META___`, {
          ...meta,
          ...prevValue,
          name,
        });

        // This creates all possible sub paths, even if levels are missing in
        // the hierarchy
        name
          .split(".")
          .reduce<string[]>((accumulator, currentValue) => {
            const prev = accumulator.at(-1);
            const next = prev ? [prev, currentValue] : [currentValue];
            accumulator.push(next.join("."));
            return accumulator;
          }, [])
          .forEach((namePart) => {
            if (!get(node, `${namePart}.___DEFINITY_TREE_META___.name`)) {
              const setChild = {
                ...get(node, `${namePart}.___DEFINITY_TREE_META___`, {}),
                name: namePart,
              } as DefinityTreeMeta;
              set(node, `${namePart}.___DEFINITY_TREE_META___`, setChild);
            }

            if (search && name.match(search)) {
              matchedSearch.push(node.___DEFINITY_TREE_META___.name);
              const namePartName = get(
                node,
                `${namePart}.___DEFINITY_TREE_META___.name`
              );

              if (typeof namePartName === "string") {
                matchedSearch.push(namePartName);
              }

              set(node, `${namePart}.___DEFINITY_TREE_META___.hasMatch`, true);

              set(node, `___DEFINITY_TREE_META___.hasMatch`, true);
            }
          });

        return node;
      },
      {
        ___DEFINITY_TREE_META___: { name: rootId },
      }
    ),
  };
};
